// components/chat/ChatInterface.js - Updated
import React, { useState, useRef, useEffect } from 'react';
import './ChatInterface.css';
import config from '../../utils/config';
import prompts from '../../utils/prompts';
import { processFile, formatFilesForPrompt } from '../../utils/fileUtils';
import { formatMessage } from '../../utils/messageFormatter';
import { useMessageHandling } from '../../hooks/useMessageHandling';
import { useFileHandling } from '../../hooks/useFileHandling';
import EmptyState from '../emptyState/EmptyState';
import FileAttachmentPanel from '../fileAttachmentPanel/FileAttachmentPanel';
import ChatInputForm from '../chatInputForm/ChatInputForm';
import MessageAvatar from '../messageAvatar/MessageAvatar';
import DownloadButton from '../downloadButton/DownloadButton';

// Loading spinner component
const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
    <span>Loading...</span>
  </div>
);

const ChatInterface = () => {
  const [userMessage, setUserMessage] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [initialInput, setInitialInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(true);
  
  const messageEndRef = useRef(null);
  const contentRef = useRef(null);

  // Initialize file handling hook
  const {
    attachedFiles,
    setAttachedFiles,
    keepFiles,
    setKeepFiles,
    fileError,
    setFileError,
    fileInputRef,
    parsedFileData,
    handleAttachmentClick,
    handleFileChange,
    handleRemoveFile,
    handleKeepFilesToggle,
    handleClearAllFiles
  } = useFileHandling({
    processFile
  });

  // Custom handleSubmit that sets userMessage immediately
  const customHandleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    const currentInput = inputValue.trim();
    if (!currentInput) return;
    
    // Immediately set the user message in the feedback window
    setUserMessage({ role: 'user', content: currentInput });
    
    // Continue with the normal submission logic
    await handleSubmit(e);
  };

  // Use message handling hook with modified setLastMessage to track last user message
  const { 
    inputValue, 
    setInputValue, 
    handleInputChange, 
    handleSubmit,
    textareaRef 
  } = useMessageHandling({
    lastMessage,
    setLastMessage,  // Pass the original setLastMessage function - the hook now handles the formatting
    initialInput,
    setInitialInput,
    isLoading,
    setIsLoading,
    attachedFiles,
    setAttachedFiles,
    keepFiles,
    formatFilesForPrompt,
    parsedFileData,
    config,
    prompts
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [userMessage, lastMessage, isLoading]);

  // Update empty state visibility
  useEffect(() => {
    setShowEmptyState(!userMessage && !lastMessage && !isLoading);
  }, [userMessage, lastMessage, isLoading]);

  // Adjust layout when files are attached or removed
  useEffect(() => {
    // Force layout recalculation when files change
    if (contentRef.current) {
      void contentRef.current.offsetHeight;
    }
  }, [attachedFiles.length]);

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // Safely render a formatted message
  const renderMessageContent = (content) => {
    const formattedContent = formatMessage(content);
    if (typeof formattedContent === 'object' && formattedContent.__html) {
      return <div dangerouslySetInnerHTML={formattedContent} />;
    }
    return formattedContent;
  };

  return (
    <div className="chat-container">
      <div className="chat-windows-container" ref={contentRef}>
        {/* User Request Window */}
        <div className="chat-window user-window">
          <div className="window-header">
            <h3>Your Feedback</h3>
          </div>
          <div className="window-content">
            {userMessage ? (
              <div className="message user">
                <MessageAvatar role='user'/>
                <div className="message-bubble">
                  <div className="message-content">
                    {renderMessageContent(userMessage.content)}
                  </div>
                </div>
              </div>
            ) : showEmptyState ? (
              <EmptyState 
                handleSuggestionClick={handleSuggestionClick} 
              />
            ) : (
              <div className="empty-message">No request yet</div>
            )}
          </div>
        </div>
        {/* Assistant Response Window with Download Button */}
        <div className="chat-window-container">
                  {/* Download Button - Only visible when lastMessage exists */}
                  {lastMessage && lastMessage.content && (
                    <div className="download-button-container">
                      <DownloadButton messageContent={lastMessage.originalContent} />
                    </div>
                  )}
                  
                  {/* Assistant Response Window */}
                  <div className="chat-window assistant-window">
                    <div className="window-header">
                      <h3>Response</h3>
                    </div>
                    <div className="window-content">
                      {lastMessage && (
                        <div className="message assistant">
                          <MessageAvatar role='assistant'/>
                          <div className="message-bubble">
                            <div className="message-content">
                              {renderMessageContent(lastMessage.content)}
                            </div>
                          </div>
                        </div>
                      )}
                      {isLoading && (
                        <div className="message assistant loading">
                          <MessageAvatar role='assistant' />
                          <div className="message-bubble loading-message">
                            <LoadingSpinner />
                          </div>
                        </div>
                      )}
                      {!lastMessage && !isLoading && (
                        <div className="empty-message">No response yet</div>
                      )}
                      <div ref={messageEndRef} />
                    </div>
                  </div>
                </div>
              </div>
      

      {/* Attached Files Display */}
      {attachedFiles.length > 0 && (
        <FileAttachmentPanel
          attachedFiles={attachedFiles}
          keepFiles={keepFiles}
          handleRemoveFile={handleRemoveFile}
          handleClearAllFiles={handleClearAllFiles}
          handleKeepFilesToggle={handleKeepFilesToggle}
        />
      )}

      {/* File Upload Error Display */}
      {fileError && (
        <div className="file-error-message">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{fileError}</span>
          <button 
            className="close-error-button" 
            onClick={() => setFileError('')}
            title="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      <ChatInputForm
        inputValue={inputValue}
        handleInputChange={handleInputChange}
        handleSubmit={customHandleSubmit}
        isLoading={isLoading}
        textareaRef={textareaRef}
        fileInputRef={fileInputRef}
        handleAttachmentClick={handleAttachmentClick}
        handleFileChange={handleFileChange}
      />
    </div>
  );
};

export default ChatInterface;